'use client';

import { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import GlobalSearch from '@/components/search/GlobalSearch';

const navLinks = [
  { label: 'Ana Sayfa', href: '/' },
  { label: 'Karikatürler', href: '/karikaturler' },
  { label: 'Komik Hikayeler', href: '/hikayeler' },
  { label: 'Fıkralar', href: '/fikralar' },
  { label: 'Popüler', href: '/#populer' },
  { label: 'Hakkımızda', href: '/hakkimizda' },
];

export default function SiteHeader() {
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  function isActive(href: string): boolean {
    if (href === '/') return pathname === '/';
    if (href === '/#populer') return false;
    return pathname.startsWith(href);
  }

  return (
    <header className="sticky top-0 z-50 border-b border-stone-200 bg-white/95 backdrop-blur">
      <div className="mx-auto flex h-[72px] max-w-[1400px] items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link href="/" className="flex items-center">
          <img src="/rsm/logo.png" alt="enkini.com" className="h-[50px] object-contain" />
        </Link>

        <nav className="hidden items-center gap-[30px] md:flex" role="navigation" aria-label="Ana menü">
          {navLinks.map((link) => (
            <Link
              key={link.label}
              href={link.href}
              className={`relative text-sm font-semibold transition-colors duration-200 pb-[5px] ${
                isActive(link.href)
                  ? 'text-orange-500 after:absolute after:bottom-[-5px] after:left-0 after:h-[3px] after:w-full after:rounded after:bg-orange-500'
                  : 'text-stone-500 hover:text-orange-500'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-5">
          <button
            onClick={() => { setSearchOpen((prev) => !prev); setMobileOpen(false); }}
            className="flex h-9 w-9 items-center justify-center text-stone-950 transition-colors hover:text-orange-500"
            aria-label={searchOpen ? 'Aramayı kapat' : 'Ara'}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
            </svg>
          </button>
          <Link
            href="/#populer"
            className="hidden items-center gap-2 rounded-full bg-orange-500 px-5 py-2.5 text-xs font-bold text-white shadow-sm transition-all duration-200 hover:bg-orange-600 hover:-translate-y-0.5 md:inline-flex"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" /><path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" /><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" /><path d="M13 15v5s3-.55 4-2c1.08-1.62 0-5 0-5" />
            </svg>
            <span>İçeriği Keşfet</span>
          </Link>
          <button
            onClick={() => { setMobileOpen((prev) => !prev); setSearchOpen(false); }}
            className="inline-flex items-center justify-center rounded-lg p-2 text-stone-950 transition-colors hover:bg-stone-100 hover:text-orange-500 md:hidden"
            aria-label={mobileOpen ? 'Menüyü kapat' : 'Menüyü aç'}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? (
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18" /><path d="m6 6 12 12" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="4" x2="20" y1="12" y2="12" /><line x1="4" x2="20" y1="6" y2="6" /><line x1="4" x2="20" y1="18" y2="18" />
              </svg>
            )}
          </button>
        </div>
      </div>

      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          mobileOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <nav
          className="border-t border-stone-200 bg-white px-4 pb-6 pt-4 sm:px-6 lg:px-8"
          role="navigation"
          aria-label="Mobil menü"
        >
          <div className="mx-auto max-w-[1400px] flex flex-col gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.label}
                href={link.href}
                onClick={() => setMobileOpen(false)}
                className={`rounded-xl px-4 py-3 text-sm font-semibold transition-all duration-200 ${
                  isActive(link.href)
                    ? 'text-orange-500 bg-orange-50'
                    : 'text-stone-600 hover:bg-stone-50 hover:text-orange-500'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              href="/#populer"
              className="mt-3 flex items-center justify-center gap-2 rounded-full bg-orange-500 px-5 py-3 text-center text-sm font-bold text-white shadow-sm transition-all duration-200 hover:bg-orange-600"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" /><path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" /><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" /><path d="M13 15v5s3-.55 4-2c1.08-1.62 0-5 0-5" />
              </svg>
              İçeriği Keşfet
            </Link>
          </div>
        </nav>
      </div>

      <GlobalSearch key={String(searchOpen)} open={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  );
}
